import {
  set as setFeature,
  getKeys as getFeatureKeys,
  getAll as getAllFeature
} from '@/libs/leaflet/indexedDB'

export type CustomGeoProperties = GeoJSON.Geometry & {
  properties: StreetProperties
}
export interface StreetProperties {
  districtId: string
  streetName: string
  selected: false
}

export type CustomFeature = GeoJSON.Feature<GeoJSON.Polygon, StreetProperties>

export const onEachFeature = (
  clickEventHandler: (e: L.LeafletMouseEvent) => void,
  selectedDistrictId: string[]
) => {
  return (feature: CustomFeature, layer: L.Layer): void => {
    layer.on({
      mouseover: e => {
        const layer = e.target as L.Path
        layer.setStyle({
          fillColor: '#7fffd4',
          fillOpacity: 0.6
        })
      },
      mouseout: e => {
        const layer = e.target as L.Path
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const feature = (layer as any).feature as CustomFeature
        if (!(feature.properties.districtId in selectedDistrictId)) {
          layer.setStyle({
            fillOpacity: 0.3
          })
        }
      },
      click: e => {
        const layer = e.target as L.Path
        clickEventHandler(e)
        layer.setStyle({
          fillOpacity: 0.6
        })
      }
    })
  }
}

export const highlightFeature = (layer: L.Path): void => {
  layer.setStyle({
    fillColor: '#7fffd4',
    fillOpacity: 0.3,
    opacity: 0.7
  })
}

export const resetHighlightFeature = (layer: L.Path): void => {
  layer.setStyle({
    fillColor: '#ffffff',
    weight: 1,
    opacity: 0.7,
    color: 'black',
    dashArray: '1'
  })
}

export const style = (
  feature: GeoJSON.Feature<GeoJSON.Geometry, StreetProperties>
): L.PathOptions => {
  return {
    fillColor: '#ffffff',
    weight: 1,
    opacity: 0.7,
    color: 'black',
    dashArray: '1',
    fillOpacity: feature.properties?.selected ? 0.8 : 0.4
  }
}

/**
 * leafletのFeatureエンティティから矩形座標を計算する
 * @param feature - leafletのFeatureエンティティ
 * @returns - 矩形座標情報(北東、北西、南東、南西)
 */
export const calcRectangularArea = (
  feature: CustomFeature
): {
  swCoordinates: number[]
  neCoordinates: number[]
  nwCoordinates: number[]
  seCoordinates: number[]
} => {
  // 最東の経度
  let easternmostLng = -180
  // 最西の経度
  let westernmostLng = 180
  // 最北の緯度
  let northernmostLat = -90
  // 最南の緯度
  let southernmostLat = 90

  for (const coordinate of feature.geometry.coordinates) {
    for (const position of coordinate) {
      // 経度
      if (position[0] > easternmostLng) {
        easternmostLng = position[0]
      }
      if (position[0] < westernmostLng) {
        westernmostLng = position[0]
      }
      // 緯度
      if (position[1] > northernmostLat) {
        northernmostLat = position[1]
      }
      if (position[1] < southernmostLat) {
        southernmostLat = position[1]
      }
    }
  }

  const neCoordinates = [easternmostLng, northernmostLat]
  const swCoordinates = [westernmostLng, southernmostLat]
  const nwCoordinates = [westernmostLng, northernmostLat]
  const seCoordinates = [easternmostLng, southernmostLat]
  return { swCoordinates, neCoordinates, nwCoordinates, seCoordinates }
}

export const setDownloadedFeature = async (feature: CustomFeature): Promise<void> => {
  await setFeature<CustomFeature>('downloadedFeature', feature.properties.districtId, feature)
}
export const getDownloadedDistrictId = async (): Promise<string[]> => {
  const res = await getFeatureKeys<string>('downloadedFeature')
  if (res !== undefined) return res
  return []
}

export const getAllDownloadedFeature = async (): Promise<CustomFeature[]> => {
  const res = await getAllFeature<CustomFeature>('downloadedFeature')
  if (res !== undefined) return res
  return []
}
